/**
 * Business-language labels for AVA MCP tool steps (AVA Assistant).
 *
 * The person using the assistant never sees a tool name: a step reads "Counted anomalies",
 * not "list_anomalies" (feedback: the assistant talks to an AVA user, not a developer).
 *
 * The server is the source of truth (MCP readiness rule: semantics live server-side). When a
 * tool.start event carries `label`, that wins. This catalogue is only the client fallback for
 * events that arrive without one, keyed by the real AVA MCP tool names.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

/** @typedef {{ verb: string, object: string }} ToolLabel */

/** @type {Readonly<Record<string, ToolLabel>>} */
export const TOOL_LABELS = Object.freeze({
  list_facilities: { verb: 'Found', object: 'the facility' },
  list_companies: { verb: 'Found', object: 'the company' },
  list_levels_areas: { verb: 'Found', object: 'the area' },
  get_data_catalog: { verb: 'Found', object: 'the data' },
  list_anomalies: { verb: 'Counted', object: 'anomalies' },
  preview_widget_data: { verb: 'Checked', object: 'the numbers' },
  propose_dashboard: { verb: 'Drafted', object: 'the dashboard' },
  apply_dashboard_plan: { verb: 'Created', object: 'the dashboard' },
  list_inspections: { verb: 'Looked up', object: 'inspection plans' },
  get_integrity_summary: { verb: 'Read', object: 'the integrity summary' },
  search_tags: { verb: 'Searched', object: 'tags' },
  list_tasks: { verb: 'Checked', object: 'tasks' },
  list_users: { verb: 'Looked up', object: 'users' },
});

/** Shown when neither the event nor the catalogue knows the tool. */
export const UNKNOWN_TOOL_LABEL = Object.freeze({ verb: 'Used', object: 'an AVA tool' });

/**
 * @param {string} tool AVA MCP tool name
 * @param {ToolLabel} [label] label sent by the server, which wins when present
 * @returns {ToolLabel}
 */
export function toolLabel(tool, label) {
  if (label && label.verb) return { verb: label.verb, object: label.object || '' };
  return TOOL_LABELS[tool] || UNKNOWN_TOOL_LABEL;
}
